import { useEffect, useState } from "react";

type ImageURL = string;
type SourcesLoaded = ImageURL[];

export default function useLazyLoad(
  elementRef: React.RefObject<HTMLElement>,
  src?: string | string[]
): SourcesLoaded {
  const [sourcesLoaded, setSourcesLoaded] = useState<SourcesLoaded>([]);

  useEffect(() => {
    let urls: ImageURL[] = [];
    if (src == null) return;

    if (Array.isArray(src)) {
      urls = src.filter((url): url is ImageURL => typeof url === "string");
    } else if (src.includes("url(")) {
      // If multiple URLs, split the string and remove empty strings
      urls = src
        .split('url("')
        .map((url) => url.replace('")', ""))
        .filter(Boolean) as ImageURL[];
    } else {
      // If single URL, just add it to the array
      urls.push(src);
    }

    if (urls.length > 0 && urls[0] !== "") {
      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              urls.forEach((url, index) => {
                if (index === 0) {
                  setSourcesLoaded([]);
                }

                const img = new Image();
                img.src = url;

                img.onload = () => {
                  setSourcesLoaded((prevSources) => [...prevSources, url]);
                  intersectionObserver.unobserve(entry.target);
                };
              });
            }
          });
        },
        { threshold: 0 }
      );

      if (elementRef.current) {
        intersectionObserver.observe(elementRef.current);
      }

      return () => {
        intersectionObserver.disconnect();
      };
    }
  }, [elementRef, src]);

  return sourcesLoaded;
}
